import React, { useState, useEffect } from 'react';
import { api } from '../../utils/api';
import FeatureTip from '../../components/FeatureTip';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { Calculator, Users, Clock, Printer, Mail, Send, Loader2 } from 'lucide-react';

const EMPLOYER_FEE_RATE = 0.3142; // Arbetsgivaravgift 31.42%

const PayrollPage = () => {
  const [payroll, setPayroll] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sendingId, setSendingId] = useState(null);
  const now = new Date();
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];
  const [periodStart, setPeriodStart] = useState(firstDay);
  const [periodEnd, setPeriodEnd] = useState(lastDay);

  const calculate = async () => {
    setLoading(true);
    try {
      const res = await api.calculatePayroll({ period_start: periodStart, period_end: periodEnd });
      setPayroll(res.data || []);
    } catch (err) {
      toast.error('Kunde inte beräkna löner');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { calculate(); }, []);

  const totalGross = payroll.reduce((s, p) => s + p.gross_salary, 0);
  const totalHours = payroll.reduce((s, p) => s + p.total_hours, 0);
  const totalEmployerFees = Math.round(totalGross * EMPLOYER_FEE_RATE);

  const handleSendPayslip = async (emp) => {
    setSendingId(emp.employee_id);
    try {
      await api.sendPayslip({
        employee_id: emp.employee_id,
        period_start: periodStart,
        period_end: periodEnd,
      });
      toast.success(`Lönespec skickad till ${emp.employee_name}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte skicka lönespec');
    } finally {
      setSendingId(null);
    }
  };

  const handleSendAll = async () => {
    for (const emp of payroll) {
      if (emp.gross_salary > 0) {
        await handleSendPayslip(emp);
      }
    }
  };

  const handlePrint = () => {
    const w = window.open('', '_blank');
    const rows = payroll.map(p =>
      `<tr><td style="padding:6px 10px;border-bottom:1px solid #eee">${p.employee_name}</td>
      <td style="padding:6px 10px;border-bottom:1px solid #eee;text-align:right">${p.regular_hours}</td>
      <td style="padding:6px 10px;border-bottom:1px solid #eee;text-align:right">${p.overtime_hours}</td>
      <td style="padding:6px 10px;border-bottom:1px solid #eee;text-align:right">${p.ob1_hours}</td>
      <td style="padding:6px 10px;border-bottom:1px solid #eee;text-align:right">${p.ob_night_hours}</td>
      <td style="padding:6px 10px;border-bottom:1px solid #eee;text-align:right">${p.total_hours}</td>
      <td style="padding:6px 10px;border-bottom:1px solid #eee;text-align:right;font-weight:600">${p.gross_salary.toLocaleString('sv-SE')} kr</td>
      <td style="padding:6px 10px;border-bottom:1px solid #eee;text-align:right">${Math.round(p.gross_salary * EMPLOYER_FEE_RATE).toLocaleString('sv-SE')} kr</td></tr>`
    ).join('');
    const html = `<html><head><title>Löneunderlag ${periodStart} — ${periodEnd}</title>
    <style>body{font-family:Arial,sans-serif;max-width:960px;margin:20px auto;color:#1a1a1a}h1{font-size:22px}table{width:100%;border-collapse:collapse}th{text-align:left;padding:8px 10px;background:#f5f5f5;border-bottom:2px solid #ccc;font-size:11px;text-transform:uppercase}td{font-size:13px}tfoot td{font-weight:bold;border-top:2px solid #333;padding:8px 10px}</style>
    </head><body><h1>Löneunderlag</h1><p style="color:#666">Period: ${periodStart} — ${periodEnd}</p>
    <table><thead><tr><th>Anställd</th><th style="text-align:right">Ordinarie</th><th style="text-align:right">Övertid</th><th style="text-align:right">OB Kväll</th><th style="text-align:right">OB Natt</th><th style="text-align:right">Totalt h</th><th style="text-align:right">Bruttolön</th><th style="text-align:right">Arb.avg (31.42%)</th></tr></thead>
    <tbody>${rows}</tbody>
    <tfoot><tr><td colspan="5">TOTALT</td><td style="text-align:right">${totalHours.toFixed(1)}</td><td style="text-align:right">${totalGross.toLocaleString('sv-SE')} kr</td><td style="text-align:right">${totalEmployerFees.toLocaleString('sv-SE')} kr</td></tr></tfoot></table>
    <p style="margin-top:24px;font-size:12px;color:#888">Total kostnad (lön + avgifter): <strong>${(totalGross + totalEmployerFees).toLocaleString('sv-SE')} kr</strong></p>
    </body></html>`;
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    w.location.href = url;
    setTimeout(() => { w.print(); URL.revokeObjectURL(url); }, 500);
  };

  return (
    <div className="space-y-6" data-testid="payroll-page">
      <FeatureTip tipId="payroll" title="Löner" body="Lönerna genereras automatiskt från godkända tidrapporter. Här ser du underlag per anställd, kan justera och generera lönespec som PDF eller mail." />
      <div className="flex flex-col sm:flex-row items-start sm:items-end gap-4 flex-wrap">
        <div className="flex gap-3 items-end flex-wrap">
          <div><Label className="text-xs">Från</Label><Input type="date" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} data-testid="payroll-start" className="w-40" /></div>
          <div><Label className="text-xs">Till</Label><Input type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} data-testid="payroll-end" className="w-40" /></div>
          <Button onClick={calculate} disabled={loading} className="bg-[hsl(16,100%,55%)] hover:bg-[hsl(16,100%,45%)] text-white" data-testid="calculate-payroll-btn">
            <Calculator size={14} className="mr-1.5" /> {loading ? 'Beräknar...' : 'Beräkna'}
          </Button>
        </div>
        {payroll.length > 0 && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={handlePrint} data-testid="print-payroll-btn">
              <Printer size={14} className="mr-1.5" /> Skriv ut
            </Button>
            <Button variant="outline" onClick={handleSendAll} data-testid="send-all-payslips-btn">
              <Send size={14} className="mr-1.5" /> Skicka alla lönespecar
            </Button>
          </div>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="bg-card border border-border rounded p-5">
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Anställda</p>
          <p className="text-2xl font-bold text-foreground mt-1">{payroll.length}</p>
        </div>
        <div className="bg-card border border-border rounded p-5">
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Totala timmar</p>
          <p className="text-2xl font-bold text-foreground mt-1">{totalHours.toFixed(1)}</p>
        </div>
        <div className="bg-card border border-border rounded p-5">
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Total bruttolön</p>
          <p className="text-2xl font-bold text-foreground mt-1">{totalGross.toLocaleString('sv-SE')} kr</p>
        </div>
        <div className="bg-card border border-border rounded p-5">
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Arbetsgivaravgift</p>
          <p className="text-2xl font-bold text-foreground mt-1">{totalEmployerFees.toLocaleString('sv-SE')} kr</p>
          <p className="text-xs text-muted-foreground mt-0.5">31.42%</p>
        </div>
        <div className="bg-card border border-border rounded p-5">
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Total kostnad</p>
          <p className="text-2xl font-bold text-[hsl(16,100%,55%)] mt-1">{(totalGross + totalEmployerFees).toLocaleString('sv-SE')} kr</p>
          <p className="text-xs text-muted-foreground mt-0.5">Lön + avgifter</p>
        </div>
      </div>

      {/* Payroll Table */}
      <div className="bg-card border border-border rounded">
        {payroll.length === 0 ? (
          <div className="p-8 text-center">
            <Users size={32} className="text-muted-foreground/30 mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">Ingen lönedata för perioden</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50 border-b border-border">
                <tr>
                  <th className="px-5 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Anställd</th>
                  <th className="px-5 py-3 text-right text-xs font-medium text-muted-foreground uppercase">Ordinarie</th>
                  <th className="px-5 py-3 text-right text-xs font-medium text-muted-foreground uppercase">Övertid</th>
                  <th className="px-5 py-3 text-right text-xs font-medium text-muted-foreground uppercase">OB Kväll</th>
                  <th className="px-5 py-3 text-right text-xs font-medium text-muted-foreground uppercase">OB Natt</th>
                  <th className="px-5 py-3 text-right text-xs font-medium text-muted-foreground uppercase">Totalt h</th>
                  <th className="px-5 py-3 text-right text-xs font-medium text-muted-foreground uppercase">Bruttolön</th>
                  <th className="px-5 py-3 text-right text-xs font-medium text-muted-foreground uppercase">Arb.avg</th>
                  <th className="px-5 py-3 text-center text-xs font-medium text-muted-foreground uppercase">Lönespec</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {payroll.map((p) => (
                  <tr key={p.employee_id} data-testid={`payroll-row-${p.employee_id}`}>
                    <td className="px-5 py-3 text-sm font-medium">{p.employee_name}</td>
                    <td className="px-5 py-3 text-sm text-right">{p.regular_hours}h</td>
                    <td className="px-5 py-3 text-sm text-right">{p.overtime_hours > 0 ? <span className="text-yellow-700 font-medium">{p.overtime_hours}h</span> : '0h'}</td>
                    <td className="px-5 py-3 text-sm text-right">{p.ob1_hours > 0 ? `${p.ob1_hours}h` : '—'}</td>
                    <td className="px-5 py-3 text-sm text-right">{p.ob_night_hours > 0 ? `${p.ob_night_hours}h` : '—'}</td>
                    <td className="px-5 py-3 text-sm text-right font-medium">{p.total_hours}h</td>
                    <td className="px-5 py-3 text-sm text-right font-bold">{p.gross_salary.toLocaleString('sv-SE')} kr</td>
                    <td className="px-5 py-3 text-sm text-right text-muted-foreground">{Math.round(p.gross_salary * EMPLOYER_FEE_RATE).toLocaleString('sv-SE')} kr</td>
                    <td className="px-5 py-3 text-center">
                      <button
                        onClick={() => handleSendPayslip(p)}
                        disabled={sendingId === p.employee_id}
                        className="p-1.5 text-muted-foreground hover:text-[hsl(16,100%,55%)] hover:bg-[hsl(16,100%,55%)]/10 rounded transition-colors disabled:opacity-50"
                        title="Skicka lönespec via e-post"
                        data-testid={`send-payslip-${p.employee_id}`}
                      >
                        {sendingId === p.employee_id ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-muted/30 border-t-2 border-border">
                  <td className="px-5 py-3 text-sm font-bold">TOTALT</td>
                  <td className="px-5 py-3 text-sm text-right font-bold">{payroll.reduce((s, p) => s + p.regular_hours, 0).toFixed(1)}h</td>
                  <td className="px-5 py-3 text-sm text-right font-bold">{payroll.reduce((s, p) => s + p.overtime_hours, 0).toFixed(1)}h</td>
                  <td className="px-5 py-3 text-sm text-right font-bold">{payroll.reduce((s, p) => s + p.ob1_hours, 0).toFixed(1)}h</td>
                  <td className="px-5 py-3 text-sm text-right font-bold">{payroll.reduce((s, p) => s + p.ob_night_hours, 0).toFixed(1)}h</td>
                  <td className="px-5 py-3 text-sm text-right font-bold">{totalHours.toFixed(1)}h</td>
                  <td className="px-5 py-3 text-sm text-right font-bold">{totalGross.toLocaleString('sv-SE')} kr</td>
                  <td className="px-5 py-3 text-sm text-right font-bold">{totalEmployerFees.toLocaleString('sv-SE')} kr</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default PayrollPage;
